import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import IconButton from "../../components/IconButton/IconButton";
import Grid from "../../config/Grid/Grid";
import styles from "./Menu.module.scss";
import MenuItemTile from "./components/MenuItemTile/MenuItemTile";
import NavigationHeader from "./components/NavigationHeader/NavigationHeader";
import DishFilterBar from "@/components/DishFilterBar/DishFilterBar";
import MenuTypeDrawer from "@/components/MenuTypeDrawer/MenuTypeDrawer";
import { useMenuData } from "@/hooks/useMenuData";
import { useDishReference } from "@/hooks/useDishReference";
import { useDishFilters } from "@/hooks/useDishFilters";
import {
  getTranslation,
  getTranslatedDescription,
} from "@/helpers/getTranslation";
import {
  ALL_MENU_TYPE_ID,
  menuDishPath,
  menuPath,
  menuTypePath,
  tablePath,
} from "@/routes/paths";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import { useGoBack } from "@/hooks/useGoBack";

/** Browse-only dish list for one menu, split into category tabs. */
function MenuTypePage() {
  const { restaurantId = "", menuTypeId = "" } = useParams<{
    restaurantId: string;
    menuTypeId: string;
  }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const navigate = useNavigate();
  const tableId = useVenuesDataStore((state) => state.tableId);

  const { dishes, groups, menuTypeGroups, loading, error } =
    useMenuData(restaurantId);

  const showAll = menuTypeId === ALL_MENU_TYPE_ID;
  const typeGroup = menuTypeGroups.find(
    (candidate) => candidate.menuType.id === menuTypeId,
  );
  const menuDishes = showAll ? dishes : (typeGroup?.dishes ?? []);
  const categoryGroups = showAll ? groups : (typeGroup?.categories ?? []);
  const title = showAll
    ? t("menu.allDishes")
    : (typeGroup?.name ?? t("menu.title"));

  // With no menu to pick, Menu redirects straight here, so back has to leave
  // the menu rather than bounce off that redirect.
  const goBack = useGoBack(
    menuTypeGroups.length > 0
      ? menuPath(restaurantId)
      : tableId
        ? tablePath(restaurantId, tableId)
        : undefined,
  );

  /** Index 0 is every dish in this menu; the rest are its categories. */
  const [selectedTab, setSelectedTab] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setSelectedTab(0);
  }, [menuTypeId]);

  const selectedCategory = categoryGroups[selectedTab - 1];
  const scopedDishes = selectedCategory ? selectedCategory.dishes : menuDishes;

  const reference = useDishReference(restaurantId);
  /** Redundant inside a single menu; see the note in OrderMenuTypePage. */
  const scopedReference = useMemo(
    () => (showAll ? reference : { ...reference, menuTypes: [] }),
    [reference, showAll],
  );
  const filters = useDishFilters(scopedDishes, scopedReference);
  const visibleDishes = filters.visibleDishes;

  const menuOptions = useMemo(
    () => [
      {
        id: ALL_MENU_TYPE_ID,
        name: t("menu.allDishes"),
        subtitle: t("menu.dishCount", { count: dishes.length }),
      },
      ...menuTypeGroups.map((group) => ({
        id: group.menuType.id,
        name: group.name,
        subtitle: t("menu.dishCount", { count: group.dishes.length }),
      })),
    ],
    [dishes.length, menuTypeGroups, t],
  );

  return (
    <Grid>
      <div className={`start-1 col-4 ${styles.menuWrapper}`}>
        <div className={styles.menuHeaderContainer}>
          <div className={styles.menuHeader}>
            <IconButton
              iconName="las la-angle-left icon-big"
              onClick={goBack}
              color="primary"
              variant="text"
            />
            {menuTypeGroups.length > 0 ? (
              <div
                className={styles.menuTitleButton}
                onClick={() => setMenuOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setMenuOpen(true);
                  }
                }}
              >
                <p className={`body-bold-large ${styles.menuTitle}`}>{title}</p>
                <i
                  className={`las la-angle-down icon-medium ${styles.menuTitleChevron}`}
                />
              </div>
            ) : (
              <p className={`body-bold-large ${styles.menuTitle}`}>{title}</p>
            )}
            <IconButton
              iconName=""
              onClick={() => {}}
              color="primary"
              variant="text"
            />
          </div>

          {categoryGroups.length > 0 ? (
            <div className={styles.menuTabs}>
              <NavigationHeader
                navigationHeaders={[
                  { label: t("menu.all"), action: () => {} },
                  ...categoryGroups.map((group) => ({
                    label: group.categoryName,
                    action: () => {},
                  })),
                ]}
                selectedTab={selectedTab}
                onTabChange={setSelectedTab}
              />
            </div>
          ) : null}
        </div>

        <div className={styles.menuContent}>
          {!showAll && !typeGroup && !loading ? (
            <p className={`body-medium ${styles.menuStateMessage}`}>
              {error ? t("order.loadError") : t("order.emptyMenu")}
            </p>
          ) : null}
          <DishFilterBar filters={filters} />

          {filters.hasActiveFilters && visibleDishes.length === 0 ? (
            <p className={`body-medium ${styles.menuStateMessage}`}>
              {t("order.filters.noResults")}
            </p>
          ) : null}

          <div className={styles.categorySection}>
            {visibleDishes.map((dish) => (
              <MenuItemTile
                key={dish.id}
                name={getTranslation(dish.translations, language)}
                description={getTranslatedDescription(
                  dish.translations,
                  language,
                )}
                price={(dish.price / 100).toFixed(2)}
                dishImage={dish.image?.url}
                onClick={() => navigate(menuDishPath(restaurantId, dish.id))}
              />
            ))}
          </div>
        </div>
      </div>

      <MenuTypeDrawer
        open={menuOpen}
        title={t("menu.selectMenu")}
        options={menuOptions}
        onSelect={(id) => {
          setMenuOpen(false);
          if (id !== menuTypeId) {
            navigate(menuTypePath(restaurantId, id), { replace: true });
          }
        }}
        onClose={() => setMenuOpen(false)}
      />
    </Grid>
  );
}

export default MenuTypePage;
